import React, { useEffect, useRef, useState } from 'react';
import { Chat, ChatProps } from './types';
import { AppState } from '../../store/rootReducer';
import { getUserId } from '../../store/auth/selectors';
import { getLanguagesList } from '../../store/languages/selectors';
import { useDispatch, connect } from 'react-redux';
import Sidebar from '../../components/Sidebar/Sidebar';
import SidebarSecond from '../../components/Sidebar/SidebarSecond';
import { getUsersList, requestedUserIds } from '../../store/users/selectors';
import UserRoles from '../../resources/enums/UserRoles';
import { requestTryGetUsersByIds, requestSendUserNotification } from '../../store/users/actions';
import { User } from '../../store/users/types';
import ChatRoom from '../../components/Chat/ChatRoom';
import { Message } from '../../components/Chat/types';
import { database } from '../../config/firebase';
import { onValue, ref, set } from 'firebase/database';
import { objectToArray } from '../../utils/Converter';
import './ChatPage.scss';
import { getCoachList, getLoggedCoach } from '../../store/coach/selectors';
import { requestGetListCoach } from '../../store/coach/actions';
import { Alert, Box } from '@mui/material';
import strings from '../../resources/strings';
import NotificationTypes from '../../resources/enums/NotificationTypes';

function getUserIdFromChat(chatId: string) {
    const ids = chatId.split('-').filter((x) => x != UserRoles.Coach);
    if (ids.length != 1) throw new Error('Invalid chat id format');
    return ids[0];
}
const ChatPage: React.FC<ChatProps> = (props) => {
    const [actualConversationId, setActualConversationId] = useState<string>('');
    const [interlocutor, setInterlocutor] = useState<User>({} as User);
    const [messages, setMessages] = useState<Message[]>([]);
    const [chats, setChats] = useState<Chat[]>([]);
    const [chatUsers, setChatUsers] = useState<User[]>([]);
    const dispatch = useDispatch();
    const mountedRef = useRef(true);

    const disabled = props.coach === undefined;

    const firebaseEvents = {
        getChats: () => {
            if (!mountedRef.current) return null;
            const data = ref(database, 'chat');
            onValue(data, (snapshot) => {
                const chats = snapshot.val();
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                const sortedChats = Object.entries(chats)
                    .map(([id, messageObj]) => ({
                        id,
                        messages: messageObj as Message[],
                    }))
                    .filter((chat) => chat.id.startsWith(UserRoles.Coach))
                    .map((chat) => ({
                        id: chat.id,
                        userId: getUserIdFromChat(chat.id),
                        messages: objectToArray(chat.messages) as Message[],
                    }))
                    .filter((chat) => {
                        const lastMessage = chat.messages[chat.messages.length - 1];
                        const lastMessageTimestamp = new Date(lastMessage.timestamp);
                        return lastMessageTimestamp >= thirtyDaysAgo;
                    })
                    .sort((a, b) => {
                        const lastMessageA = a.messages[a.messages.length - 1];
                        const lastMessageB = b.messages[b.messages.length - 1];
                        return Number(lastMessageB.id) - Number(lastMessageA.id);
                    });
                setChats(sortedChats);
            });
        },
        sendMessage: (message: Message) => {
            if (interlocutor == null) return;
            set(ref(database, `chat/coach-${actualConversationId}/${new Date().getTime()}`), message);
            dispatch(requestSendUserNotification(NotificationTypes.Message, interlocutor.uid, 'coach'));
        },
    };

    useEffect(() => {
        dispatch(requestGetListCoach());
        if (mountedRef.current) firebaseEvents.getChats();
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const chatMessages = chats.find((x) => x.id == `coach-${interlocutor?.uid}`)?.messages;
        setMessages(objectToArray(chatMessages ?? {}) as Message[]);
        return () => {
            mountedRef.current = false;
        };
    }, [chats, interlocutor]);

    useEffect(() => {
        const users = [] as User[];
        const missingIds = [] as string[];
        for (const c of chats) {
            const user = props.users.find((x) => x.uid == c.userId);
            if (user) {
                users.push(user);
            } else {
                if (props.requestedUserIds.every((uid) => c.userId != uid)) missingIds.push(c.userId);
            }
        }
        if (missingIds.length > 0) {
            dispatch(requestTryGetUsersByIds(missingIds, [UserRoles.User]));
        }
        setChatUsers(users);
        if (actualConversationId == null || actualConversationId == '') {
            setInterlocutor(users[0]);
            setActualConversationId(users[0]?.uid);
        }
        return () => {
            mountedRef.current = false;
        };
    }, [chats, props.users]);

    return (
        <div className="content">
            <Sidebar />
            {props.coach && (
                <SidebarSecond
                    data={chatUsers?.map((x) => {
                        const title: Record<string, string> = {};
                        title['en'] = x.userName ?? '';
                        const currentUserChat = objectToArray(
                            chats.find((chat) => chat.userId == x.uid)?.messages ?? {},
                        ) as Message[];
                        const length = currentUserChat.length;
                        return {
                            id: x.uid,
                            title: title,
                            isAnswer: length > 0 && currentUserChat[length - 1]?.idFrom === x.uid,
                        };
                    })}
                    actualItemId={actualConversationId}
                    title="Chat"
                    setActualItemId={(id: string) => {
                        setActualConversationId(props.users.find((el) => el.uid === id)?.uid ?? '');
                        setInterlocutor((props.users.find((el) => el.uid === id) as User) ?? ({} as User));
                    }}
                    actualLanguage={'en'}
                    hideStatus
                ></SidebarSecond>
            )}

            {disabled && (
                <Box sx={{ pt: 2, pl: 2, width: '100%' }}>
                    <Alert variant="outlined" severity="info" sx={{ fontSize: 14, maxWidth: 800 }}>
                        {strings.coachPage.meetings.noCoachNotification}
                    </Alert>
                </Box>
            )}
            {props.coach && (
                <ChatRoom
                    userId={props.userId}
                    interlocutor={interlocutor}
                    messages={messages}
                    setMessages={setMessages}
                    sendMessage={firebaseEvents.sendMessage}
                    coaches={props.coaches}
                />
            )}
        </div>
    );
};

function mapStateToProps(state: AppState) {
    return {
        userId: getUserId(state),
        languages: getLanguagesList(state),
        users: getUsersList(state),
        coaches: getCoachList(state),
        coach: getLoggedCoach(state),
        requestedUserIds: requestedUserIds(state),
    };
}

export default connect(mapStateToProps)(ChatPage);
